import React, { useState } from 'react';
import { FinancialConfig } from './FinancialConfig.js';
import jsPDF from 'jspdf';

const SMSFComplianceDashboard = ({ smsfState, setSmsfState }) => {
    // Get data from smsfState
    const fundBalance = smsfState?.smsfData?.balance || smsfState?.smsfData?.fundBalance || 0;
    const concessionalUsed = smsfState?.smsfData?.concessionalUsed || 0;
    const nonConcessionalUsed = smsfState?.smsfData?.nonConcessionalUsed || 0;
    const yourTBCUsed = smsfState?.smsfData?.yourTbcUsed || 0;
    const partnerTBCUsed = smsfState?.smsfData?.partnerTbcUsed || 0;
    const inHouseAssets = smsfState?.smsfData?.inHouseAssets || 0;
    
    // Compliance calculations
    const concessionalCap = FinancialConfig?.CONTRIBUTION_CAPS?.CONCESSIONAL || 32500;
    const nonConcessionalCap = FinancialConfig?.CONTRIBUTION_CAPS?.NON_CONCESSIONAL || 130000;
    const tbcLimit = FinancialConfig?.TRANSFER_BALANCE_CAP || 2100000;
    
    const concessionalPercent = (concessionalUsed / concessionalCap) * 100;
    const nonConcessionalPercent = (nonConcessionalUsed / nonConcessionalCap) * 100;
    const tbcPercent = ((yourTBCUsed + partnerTBCUsed) / (tbcLimit * 2)) * 100;
    const inHousePercent = fundBalance > 0 ? (inHouseAssets / fundBalance) * 100 : 0;
    
    // Overall compliance status
    const issues = [];
    if (concessionalPercent > 100) issues.push('Concessional cap exceeded');
    if (nonConcessionalPercent > 100) issues.push('Non-concessional cap exceeded');
    if (tbcPercent > 100) issues.push('TBC limit exceeded');
    if (inHousePercent > 5) issues.push('In-house assets exceed 5%');
    
    const overallStatus = issues.length === 0 ? 'green' : issues.length <= 2 ? 'yellow' : 'red';
    const statusText = {
        green: '✅ Compliant',
        yellow: '⚠️ Warnings',
        red: '❌ Breaches'
    };
    
    // Action items
    const actionItems = [
        ...issues.map(issue => ({ type: 'breach', text: issue, priority: 'High' })),
        ...(concessionalPercent > 80 && concessionalPercent <= 100 ? [{ type: 'warning', text: 'Approaching concessional cap', priority: 'Medium' }] : []),
        ...(tbcPercent > 80 && tbcPercent <= 100 ? [{ type: 'warning', text: 'Approaching TBC limit', priority: 'Medium' }] : []),
        ...(inHousePercent > 3 && inHousePercent <= 5 ? [{ type: 'warning', text: 'In-house assets approaching 5% limit', priority: 'Medium' }] : [])
    ];
    
    // Export PDF
    const exportComplianceReport = () => {
        const doc = new jsPDF();
        
        // Title
        doc.setFontSize(18);
        doc.setFont(undefined, 'bold');
        doc.text('SMSF Compliance Report', 105, 20, { align: 'center' });
        
        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        doc.text(`Generated: ${new Date().toLocaleDateString('en-AU')}`, 105, 28, { align: 'center' });
        
        let y = 40;
        
        // Overall Status
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text(`Overall Status: ${statusText[overallStatus]}`, 14, y);
        y += 10;
        
        // Contribution Caps
        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.text('Contribution Caps', 14, y);
        y += 6;
        doc.setFontSize(9);
        doc.setFont(undefined, 'normal');
        doc.text(`Concessional: $${concessionalUsed.toLocaleString()} / $${concessionalCap.toLocaleString()} (${concessionalPercent.toFixed(1)}%)`, 14, y);
        y += 5;
        doc.text(`Non-Concessional: $${nonConcessionalUsed.toLocaleString()} / $${nonConcessionalCap.toLocaleString()} (${nonConcessionalPercent.toFixed(1)}%)`, 14, y);
        y += 10;
        
        // TBC
        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.text('Transfer Balance Cap', 14, y);
        y += 6;
        doc.setFontSize(9);
        doc.setFont(undefined, 'normal');
        doc.text(`TBC Used: $${(yourTBCUsed + partnerTBCUsed).toLocaleString()} / $${(tbcLimit * 2).toLocaleString()} (${tbcPercent.toFixed(1)}%)`, 14, y);
        y += 10;
        
        // In-House Assets
        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.text('In-House Assets', 14, y);
        y += 6;
        doc.setFontSize(9);
        doc.setFont(undefined, 'normal');
        doc.text(`In-House Assets: $${inHouseAssets.toLocaleString()} (${inHousePercent.toFixed(2)}% of fund)`, 14, y);
        y += 5;
        doc.text(`5% Limit: ${inHousePercent <= 5 ? 'Compliant ✓' : 'Breach ✗'}`, 14, y);
        y += 10;
        
        // Action Items
        if (actionItems.length > 0) {
            doc.setFontSize(11);
            doc.setFont(undefined, 'bold');
            doc.text('Action Items', 14, y);
            y += 6;
            doc.setFontSize(9);
            doc.setFont(undefined, 'normal');
            actionItems.forEach(item => {
                doc.text(`• ${item.text} (${item.priority})`, 14, y);
                y += 5;
            });
        }
        
        doc.save(`SMSF_Compliance_Report_${new Date().toISOString().split('T')[0]}.pdf`);
    };
    
    return (
        <div className="space-y-6 p-6 bg-gray-50 rounded-lg shadow-inner">
            <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-800">Compliance Dashboard</h2>
                {/* Individual PDF export removed - use comprehensive SMSF PDF at top of page */}
            </div>
            
            {/* Overall Status */}
            <div className={`p-6 rounded-lg shadow-md ${
                overallStatus === 'green' ? 'bg-green-50 border-2 border-green-300' :
                overallStatus === 'yellow' ? 'bg-yellow-50 border-2 border-yellow-300' :
                'bg-red-50 border-2 border-red-300'
            }`}>
                <div className="flex items-center justify-between">
                    <h3 className="text-xl font-bold">
                        {statusText[overallStatus]}
                    </h3>
                    <div className={`text-4xl ${
                        overallStatus === 'green' ? 'text-green-600' :
                        overallStatus === 'yellow' ? 'text-yellow-600' :
                        'text-red-600'
                    }`}>
                        {overallStatus === 'green' ? '✅' : overallStatus === 'yellow' ? '⚠️' : '❌'}
                    </div>
                </div>
                {issues.length > 0 && (
                    <p className="text-sm mt-2 text-gray-700">
                        {issues.length} issue{issues.length > 1 ? 's' : ''} requiring attention
                    </p>
                )}
            </div>
            
            {/* Contribution Caps */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Contribution Caps</h3>
                <div className="space-y-4">
                    <div>
                        <div className="flex justify-between mb-2">
                            <span className="text-sm font-medium">Concessional Contributions</span>
                            <span className={`text-sm font-bold ${concessionalPercent > 100 ? 'text-red-600' : 'text-gray-700'}`}>
                                ${concessionalUsed.toLocaleString()} / ${concessionalCap.toLocaleString()}
                            </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-4">
                            <div 
                                className={`h-4 rounded-full ${concessionalPercent > 100 ? 'bg-red-600' : concessionalPercent > 80 ? 'bg-yellow-500' : 'bg-green-500'}`}
                                style={{ width: `${Math.min(concessionalPercent, 100)}%` }}
                            ></div>
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                            {concessionalPercent.toFixed(1)}% used | ${(concessionalCap - concessionalUsed).toLocaleString()} remaining
                        </div>
                    </div>
                    
                    <div>
                        <div className="flex justify-between mb-2">
                            <span className="text-sm font-medium">Non-Concessional Contributions</span>
                            <span className={`text-sm font-bold ${nonConcessionalPercent > 100 ? 'text-red-600' : 'text-gray-700'}`}>
                                ${nonConcessionalUsed.toLocaleString()} / ${nonConcessionalCap.toLocaleString()}
                            </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-4">
                            <div 
                                className={`h-4 rounded-full ${nonConcessionalPercent > 100 ? 'bg-red-600' : nonConcessionalPercent > 80 ? 'bg-yellow-500' : 'bg-green-500'}`}
                                style={{ width: `${Math.min(nonConcessionalPercent, 100)}%` }}
                            ></div>
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                            {nonConcessionalPercent.toFixed(1)}% used | ${(nonConcessionalCap - nonConcessionalUsed).toLocaleString()} remaining
                        </div>
                    </div>
                </div>
            </div>
            
            {/* TBC Monitor */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Transfer Balance Cap (TBC) Monitor</h3>
                <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                            <div className="text-sm text-gray-600">Your TBC Used</div>
                            <div className="text-2xl font-bold text-blue-700">${yourTBCUsed.toLocaleString()}</div>
                            <div className="text-xs text-gray-500">{((yourTBCUsed / tbcLimit) * 100).toFixed(1)}% of ${tbcLimit.toLocaleString()}</div>
                        </div>
                        <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                            <div className="text-sm text-gray-600">Partner TBC Used</div>
                            <div className="text-2xl font-bold text-purple-700">${partnerTBCUsed.toLocaleString()}</div>
                            <div className="text-xs text-gray-500">{((partnerTBCUsed / tbcLimit) * 100).toFixed(1)}% of ${tbcLimit.toLocaleString()}</div>
                        </div>
                    </div>
                    <div className={`p-4 rounded-lg border ${tbcPercent > 100 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
                        <div className="flex justify-between items-center">
                            <span className="font-medium">Total TBC Usage</span>
                            <span className={`text-lg font-bold ${tbcPercent > 100 ? 'text-red-700' : 'text-green-700'}`}>
                                {tbcPercent.toFixed(1)}%
                            </span>
                        </div>
                    </div>
                </div>
            </div>
            
            {/* In-House Assets */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">In-House Assets (5% Limit)</h3>
                <div className="space-y-3">
                    <div className="flex justify-between mb-2">
                        <span className="text-sm font-medium">In-House Assets</span>
                        <span className={`text-sm font-bold ${inHousePercent > 5 ? 'text-red-600' : 'text-gray-700'}`}>
                            ${inHouseAssets.toLocaleString()} / ${fundBalance.toLocaleString()}
                        </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-4">
                        <div 
                            className={`h-4 rounded-full ${inHousePercent > 5 ? 'bg-red-600' : inHousePercent > 3 ? 'bg-yellow-500' : 'bg-green-500'}`}
                            style={{ width: `${Math.min((inHousePercent / 5) * 100, 100)}%` }}
                        ></div>
                    </div>
                    <div className="text-xs text-gray-500">
                        {inHousePercent.toFixed(2)}% of fund | 5% limit {inHousePercent <= 5 ? '✅' : '❌'}
                    </div>
                </div>
            </div>
            
            {/* Sole Purpose Test */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Sole Purpose Test</h3>
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="font-semibold text-green-800">Status: Pass ✅</div>
                            <p className="text-sm text-green-700 mt-1">
                                Fund is maintained for the sole purpose of providing retirement benefits
                            </p>
                        </div>
                    </div>
                </div>
                <div className="mt-3 text-xs text-gray-600">
                    <p><strong>Key requirements:</strong></p>
                    <ul className="list-disc ml-5 mt-1 space-y-1">
                        <li>No current-day benefits to members</li>
                        <li>No personal use of fund assets</li>
                        <li>All investments at arm's length</li>
                        <li>Proper documentation maintained</li>
                    </ul>
                </div>
            </div>
            
            {/* Investment Strategy */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Investment Strategy</h3>
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="font-semibold text-blue-800">Review Due Date</div>
                            <p className="text-sm text-blue-700 mt-1">
                                Annual review required (next review: 30 June 2026)
                            </p>
                        </div>
                        <button className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm">
                            Update Strategy
                        </button>
                    </div>
                </div>
            </div>
            
            {/* Financial Statements */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Financial Statements</h3>
                <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                        <div className="text-sm text-gray-600 mb-1">FY 2024 Lodgment</div>
                        <div className="text-lg font-bold text-green-700">✅ Lodged</div>
                        <div className="text-xs text-gray-500 mt-1">Lodged: 31 Oct 2024</div>
                    </div>
                    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <div className="text-sm text-gray-600 mb-1">FY 2025 Lodgment</div>
                        <div className="text-lg font-bold text-yellow-700">⏳ Due</div>
                        <div className="text-xs text-gray-500 mt-1">Due: 31 Oct 2025</div>
                    </div>
                </div>
            </div>
            
            {/* Audit Status */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Audit Status</h3>
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="font-semibold text-green-800">Last Audit: 15 November 2024 ✅</div>
                            <p className="text-sm text-green-700 mt-1">
                                Auditor: Smith & Co Audit Services
                            </p>
                            <p className="text-xs text-gray-600 mt-1">
                                Next audit due: October 2025
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Action Items */}
            {actionItems.length > 0 && (
                <div className="bg-white p-6 rounded-lg shadow-md">
                    <h3 className="text-lg font-semibold mb-4">Action Items Requiring Attention</h3>
                    <div className="space-y-3">
                        {actionItems.map((item, index) => (
                            <div key={index} className={`p-4 border-l-4 rounded-lg ${
                                item.priority === 'High' ? 'border-red-500 bg-red-50' : 
                                item.priority === 'Medium' ? 'border-yellow-500 bg-yellow-50' : 
                                'border-blue-500 bg-blue-50'
                            }`}>
                                <div className="flex items-center justify-between">
                                    <div>
                                        <div className={`font-semibold ${
                                            item.priority === 'High' ? 'text-red-800' : 
                                            item.priority === 'Medium' ? 'text-yellow-800' : 
                                            'text-blue-800'
                                        }`}>
                                            {item.text}
                                        </div>
                                    </div>
                                    <span className={`px-2 py-1 text-xs rounded ${
                                        item.priority === 'High' ? 'bg-red-200 text-red-800' : 
                                        item.priority === 'Medium' ? 'bg-yellow-200 text-yellow-800' : 
                                        'bg-blue-200 text-blue-800'
                                    }`}>
                                        {item.priority}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default SMSFComplianceDashboard;

